import type {TreeNode} from '@/types';


export function getCardStyle(node: TreeNode, isCategory: boolean): React.CSSProperties {
    const base: React.CSSProperties = {
        background: 'rgba(255,255,255,0.98)',
        border: '1px solid rgba(15,23,42,0.10)',
        borderRadius: 12,
        padding: '12px 16px',
        boxShadow: '0 4px 16px rgba(15,23,42,0.08)',
        position: 'relative',
        cursor: 'pointer',
        flexShrink: 0,
    };

    const nodeId = String(node.id).toLowerCase();
    const nodeType = String(node.type || '').toLowerCase();

    if (isCategory) {
        base.width = 360;
        base.minHeight = 150;
    } else if (nodeId === 'top100ka') {
        base.width = 450;
        base.minHeight = 210;
    } else if (nodeId === 'root' || nodeType === 'surgery') {
        base.width = 450;
    } else if (nodeType === 'platform' || /^(mip|ae|bio|wch)$/.test(nodeId)) {
        base.width = 450;
        base.minHeight = 250;
    } else {
        base.width = 432;
        base.minHeight = 200;
    }
    // Status border
    if (node.status === 'critical') {
        base.borderLeft = '6px solid #dc2626';
        base.animation = 'pulse-red 2.2s infinite ease-in-out';
    } else if (isCategory) {
        base.borderLeft = 'none';
    } else {
        base.borderLeft = `6px solid ${node.status === 'good' ? '#111827' : node.status === 'warning' ? '#f59e0b' : '#e2e8f0'}`;
    }
    return base;
}


export function getMetricNames(node: TreeNode, baseName: string): string[] {
    const nodeId = String(node.id || '').toLowerCase();
    const prefix = nodeId.includes('-vbp-province-') || nodeId.endsWith('-vbp')
        ? 'ims_vbp_'
        : nodeId.includes('-nonvbp-province-') || nodeId.endsWith('-nonvbp')
            ? 'ims_nonvbp_'
            : '';
    const fallback = baseName.startsWith('ims_') ? baseName : `ims_${baseName}`;
    return prefix ? [`${prefix}${baseName.replace('ims_', '')}`, fallback] : [fallback];
}

/** Extract 6-month trend data (actual + plan) from node metrics.
 *  Returns null when no real trend data is available (MiniSparkline will use fallback generation). */
export function getTrendData(
    node: TreeNode,
): { actual: (number)[]; plan: (number)[] } {
    const findMetric = (name: string) => {
        const names = getMetricNames(node, name);
        const metrics = Array.isArray(node.metrics) ? node.metrics : [];
        for (const n of names) {
            const m = metrics.find((x) => x.metric_name === n);
            if (m) return m;
        }
        return null;
    };

    const actualMetrics = [
        findMetric('ims_amt_mtd_lm5'),
        findMetric('ims_amt_mtd_lm4'),
        findMetric('ims_amt_mtd_lm3'),
        findMetric('ims_amt_mtd_lm2'),
        findMetric('ims_amt_mtd_lm1'),
        findMetric('ims_amt_mtd'),
    ];

    const planMetrics = [
        findMetric('ims_bp_amt_mtd_lm5'),
        findMetric('ims_bp_amt_mtd_lm4'),
        findMetric('ims_bp_amt_mtd_lm3'),
        findMetric('ims_bp_amt_mtd_lm2'),
        findMetric('ims_bp_amt_mtd_lm1'),
        findMetric('ims_bp_amt_mtd'),
    ];

    const actualValues = actualMetrics.map((m) => m?.value ?? 0);
    const planValues = planMetrics.map((m) => m?.value ?? 0);

    return {
        actual: actualValues,
        plan: planValues,
    };
}


/**
 * 根据指标名称和值计算颜色代码
 *
 * 返回值:
 *   1 = 灰色/中性
 *   2 = 绿色/达标
 *   3 = 红色/未达标
 *
 * 判断规则:
 *   - 达成率 (_achi): >=100% 为 2, <98% 为 3
 *   - 增长率 (growth): >0 为 2, <0 为 3
 *   - 差值 (gap, 非百分比): >0 为 2, <0 为 3
 *   - 其他: 1
 */
export function computeColorCode(metricName: string, s_value: number | null | undefined): number {
    if (s_value == null) {
        return 1;
    }
    const value = Number(s_value.toFixed(1))
    const percentage = Number((s_value * 100).toFixed(1));

    const name = metricName.toLowerCase();

    if (name == "fulfillment_achi") return 1;

    if (name.endsWith('_achi') || name.includes("_achi_")) {
        if (percentage >= 100) return 2;
        if (percentage < 100) return 3;
        return 1;
    }

    if (name.includes('growth')) {
        if (percentage > 0) return 2;
        if (percentage < 0) return 3;
        return 1;
    }

    if (name.includes('gap') && !name.endsWith('_pct')) {
        if (value > 0) return 2;
        if (value < 0) return 3;
        return 1;
    }

    return 1;
}